import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
// Description block for the task card: safe markdown preview, inline editor
// with a formatting toolbar and an editor/preview toggle, save through the
// same updateTask mutation as the form modal.
import { useRef, useState, type ReactElement } from 'react';
import { Alert, Button, Empty, Input, Segmented, Tooltip, Typography } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import {
  BoldOutlined,
  CodeOutlined,
  FontSizeOutlined,
  ItalicOutlined,
  MessageOutlined,
  OrderedListOutlined,
  StrikethroughOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';

import { BoundedMarkdownRenderer } from '../../ai-chat/components/BoundedMarkdownRenderer.js';
import { useUpdateTaskMutation } from '../hooks/useTaskMutations.js';
import { applyMarkdownFormat, type MarkdownFormatKind } from '../lib/markdownFormat.js';

// Same limit as the description field in TaskFormModal.
const DESCRIPTION_MAX_LENGTH = 10_000;

const { Text } = Typography;

type EditorMode = 'editor' | 'preview';

interface ToolbarAction {
  kind: MarkdownFormatKind;
  label: string;
  icon: ReactElement;
}

const TOOLBAR_ACTIONS: ToolbarAction[] = [
  {
    kind: 'bold',
    get label() {
      return i18n.t('crm.features.map.features.tasks.components.taskdescriptionsection.85');
    },
    icon: <BoldOutlined />,
  },
  {
    kind: 'italic',
    get label() {
      return i18n.t('crm.features.map.features.tasks.components.taskdescriptionsection.86');
    },
    icon: <ItalicOutlined />,
  },
  {
    kind: 'strike',
    get label() {
      return i18n.t('crm.features.map.features.tasks.components.taskdescriptionsection.87');
    },
    icon: <StrikethroughOutlined />,
  },
  {
    kind: 'code',
    get label() {
      return i18n.t('crm.features.map.features.tasks.components.taskdescriptionsection.88');
    },
    icon: <CodeOutlined />,
  },
  {
    kind: 'heading',
    get label() {
      return i18n.t('crm.features.map.features.tasks.components.taskdescriptionsection.89');
    },
    icon: <FontSizeOutlined />,
  },
  {
    kind: 'bullet',
    get label() {
      return i18n.t('crm.features.map.features.tasks.components.taskdescriptionsection.90');
    },
    icon: <UnorderedListOutlined />,
  },
  {
    kind: 'numbered',
    get label() {
      return i18n.t('crm.features.map.features.tasks.components.taskdescriptionsection.91');
    },
    icon: <OrderedListOutlined />,
  },
  {
    kind: 'quote',
    get label() {
      return i18n.t('crm.features.map.features.tasks.components.taskdescriptionsection.92');
    },
    icon: <MessageOutlined />,
  },
];

const MODE_OPTIONS = [
  {
    get label() {
      return i18n.t('crm.features.map.features.tasks.components.taskdescriptionsection.93');
    },
    value: 'editor',
  },
  {
    get label() {
      return i18n.t('crm.features.map.features.tasks.components.taskdescriptionsection.94');
    },
    value: 'preview',
  },
];

interface TaskDescriptionSectionProps {
  taskId: string;
  description: string | null | undefined;
}

export function TaskDescriptionSection({
  taskId,
  description,
}: TaskDescriptionSectionProps): ReactElement {
  useI18nSubscription();
  const updateMut = useUpdateTaskMutation();
  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState<EditorMode>('editor');
  const [draft, setDraft] = useState('');
  const [formatWarning, setFormatWarning] = useState(false);
  const textareaRef = useRef<TextAreaRef | null>(null);

  const startEdit = (): void => {
    setDraft(description ?? '');
    setMode('editor');
    setFormatWarning(false);
    setEditing(true);
  };

  const cancelEdit = (): void => {
    setEditing(false);
    setMode('editor');
    setFormatWarning(false);
    updateMut.reset();
  };

  const save = (): void => {
    // Empty input clears the field, same as the form modal.
    updateMut.mutate(
      { id: taskId, input: { description: draft.trim() ? draft : null } },
      {
        onSuccess: () => {
          setEditing(false);
          setMode('editor');
        },
      },
    );
  };

  const handleFormat = (kind: MarkdownFormatKind): void => {
    const textarea = textareaRef.current?.resizableTextArea?.textArea;
    if (!textarea) return;
    const result = applyMarkdownFormat(draft, textarea.selectionStart, textarea.selectionEnd, kind);
    // The textarea is capped at DESCRIPTION_MAX_LENGTH, but formatting adds
    // markdown characters. Block actions that would exceed the cap: the server
    // would reject the save anyway.
    if (result.text.length > DESCRIPTION_MAX_LENGTH) {
      setFormatWarning(true);
      return;
    }
    setFormatWarning(false);
    setDraft(result.text);
    // Restore focus and the selection once the state update is in the DOM.
    window.setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(result.selectionStart, result.selectionEnd);
    }, 0);
  };

  return (
    <div className="task-description-section">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h2 className="entity-section-title">
          {i18n.t('crm.features.features.tasks.components.taskdescriptionsection.содержание')}
        </h2>
        {!editing ? (
          <Button size="small" onClick={startEdit}>
            {description?.trim()
              ? i18n.t('crm.features.features.tasks.components.taskdescriptionsection.изменить')
              : i18n.t('crm.features.features.tasks.components.taskdescriptionsection.добавить')}
          </Button>
        ) : null}
      </div>

      {updateMut.error ? (
        <Alert
          type="error"
          showIcon
          closable
          className="mb-3"
          title={i18n.t(
            'crm.features.features.tasks.components.taskdescriptionsection.не_удалось_сохранить_содержание',
          )}
          description={updateMut.error.message}
          onClose={() => updateMut.reset()}
        />
      ) : null}

      {editing ? (
        <>
          <div className="mb-2">
            <Segmented
              size="small"
              options={MODE_OPTIONS}
              value={mode}
              disabled={updateMut.isPending}
              onChange={(value) => setMode(value as EditorMode)}
            />
          </div>
          {mode === 'editor' ? (
            <>
              {formatWarning ? (
                <Alert
                  type="warning"
                  showIcon
                  className="mb-2"
                  title={i18n.t(
                    'crm.features.dynamic.features.tasks.components.taskdescriptionsection.75',
                    {
                      p0: DESCRIPTION_MAX_LENGTH.toLocaleString(
                        i18n.locale === 'en' ? 'en-US' : 'ru-RU',
                      ),
                    },
                  )}
                />
              ) : null}
              <div className="task-md-toolbar mb-2">
                {TOOLBAR_ACTIONS.map((action) => (
                  <Tooltip key={action.kind} title={action.label}>
                    <Button
                      type="text"
                      size="small"
                      aria-label={action.label}
                      icon={action.icon}
                      disabled={updateMut.isPending}
                      // Keep the textarea focused: a click would otherwise
                      // clear the current selection before formatting.
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => handleFormat(action.kind)}
                    />
                  </Tooltip>
                ))}
              </div>
              <Input.TextArea
                ref={textareaRef}
                value={draft}
                onChange={(event) => {
                  setDraft(event.target.value);
                  setFormatWarning(false);
                }}
                rows={6}
                maxLength={DESCRIPTION_MAX_LENGTH}
                placeholder={i18n.t(
                  'crm.features.features.tasks.components.taskdescriptionsection.опишите_содержание_задачи_в_markdown',
                )}
                disabled={updateMut.isPending}
                autoFocus
              />
            </>
          ) : draft.trim() ? (
            <div className="task-md-preview">
              <BoundedMarkdownRenderer content={draft} isStreaming={false} />
            </div>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={i18n.t(
                'crm.features.features.tasks.components.taskdescriptionsection.превью_пусто',
              )}
            />
          )}
          <div className="mt-2 flex items-center gap-3">
            <Button
              type="primary"
              size="small"
              loading={updateMut.isPending}
              disabled={updateMut.isPending}
              onClick={save}
            >
              {i18n.t('crm.features.features.tasks.components.taskdescriptionsection.сохранить')}
            </Button>
            <Button size="small" disabled={updateMut.isPending} onClick={cancelEdit}>
              {i18n.t('crm.features.features.tasks.components.taskdescriptionsection.отмена')}
            </Button>
            {updateMut.isPending ? (
              <Text type="secondary" className="text-xs">
                {i18n.t('crm.features.features.tasks.components.taskdescriptionsection.сохранение')}
              </Text>
            ) : null}
          </div>
        </>
      ) : description?.trim() ? (
        <BoundedMarkdownRenderer content={description} isStreaming={false} />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={i18n.t(
            'crm.features.features.tasks.components.taskdescriptionsection.содержание_еще_не_добавлено',
          )}
        />
      )}
    </div>
  );
}
